import {useCallback, useEffect, useState} from 'react';
import {userAPI} from '@/core/api';
import {UserService} from '@/utils/services/user.service';
import {OrganizationAttendeesContributionsService} from '@/utils/services/organization-attendeesContributions';
import {IContributionResponse} from '@/utils/types';
import {initialAttendeeContributionsData} from '@/utils/constants';
import {getErrorMessage} from '@/utils/helpers/error';

export const useEventContributions = (userId?: number, eventId?: number) => {
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState<{
    attendeeId?: number;
    contributions?: IContributionResponse;
    error?: string;
  }>();
  const refresh = useCallback(() => setRevision((value) => value + 1), []);

  useEffect(() => {
    setResult(undefined);
    if (!userId || !eventId) return;
    let cancelled = false;

    const load = async () => {
      try {
        const {data: session} = await UserService.ensureUserHaveAttendeeIdentity(eventId);
        if (cancelled) return;
        if (!session.attendeeId) throw new Error('Unable to resolve attendee for this event');
        const {data: contributions} = await OrganizationAttendeesContributionsService.getAttendeesContributionsList(
          session.attendeeId,
          userAPI
        );
        if (!cancelled) {
          setResult({attendeeId: session.attendeeId, contributions});
        }
      } catch (error) {
        const message = getErrorMessage(error);
        if (!cancelled) {
          setResult({
            error: typeof message === 'string' ? message : 'Unable to load contributions for this event',
          });
        }
      }
    };

    // eslint-disable-next-line no-void
    void load();
    return () => {
      cancelled = true;
    };
  }, [userId, eventId, revision]);

  return {
    attendeeId: result?.attendeeId,
    contributions: result?.contributions ?? initialAttendeeContributionsData,
    isLoading: !!userId && !!eventId && !result,
    error: result?.error,
    refresh,
  };
};
